import { Nfc } from "lucide-react"
import { useState } from "react"
import type { Hinweis } from "./TerminalApp"
import { useChipLeser, useUhr } from "@/components/terminal/hooks"
import { antwortDetails, setzeSitzung, terminalApi, type TerminalStatus } from "@/lib/terminalApi"
import { cn } from "@/lib/format"

interface SperreProps {
  status: TerminalStatus
  hinweis?: Hinweis
  onAngemeldet: (name: string, sitzungSekunden: number) => void
  onHinweis: (h?: Hinweis) => void
}

/** Sperrbildschirm: Chip an den Leser halten (Leser tippt Nummer + Enter). */
export function Sperre({ status, hinweis, onAngemeldet, onHinweis }: SperreProps) {
  const uhr = useUhr()
  const [manuell, setManuell] = useState(false)
  const [nummer, setNummer] = useState("")
  const [laeuft, setLaeuft] = useState(false)

  async function anmelden(chip: string) {
    if (laeuft || !chip.trim()) return
    setLaeuft(true)
    try {
      const r = await terminalApi.post<{ sitzung: string; bediener: { name: string }; sitzung_sekunden: number }>(
        "/anmelden", { chip },
      )
      setzeSitzung(r.data.sitzung)
      onAngemeldet(r.data.bediener.name, r.data.sitzung_sekunden)
    } catch (e) {
      const d = antwortDetails(e)
      onHinweis({ text: d.detail, chipnummer: d.chipnummer, ton: "fehler" })
    } finally {
      setLaeuft(false)
      setNummer("")
    }
  }
  useChipLeser((chip) => void anmelden(chip), !manuell)

  const zeit = uhr.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  const datum = uhr.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })
  return (
    <main className="flex min-h-screen flex-col bg-page">
      <header className="flex items-center justify-between px-8 py-5 text-ink2">
        <span className="font-mono text-sm uppercase tracking-[0.08em]">{status.anlage}</span>
        <span className="text-sm">Betriebstagebuch</span>
      </header>
      <div className="flex flex-1 flex-col items-center justify-center gap-8 px-6 text-center">
        <div>
          <div className="text-7xl font-semibold tabular tracking-tight">{zeit}</div>
          <div className="mt-1 text-xl text-ink2">{datum}</div>
          <div className="mt-3 inline-flex rounded-full bg-surface2 px-4 py-1.5 text-lg">
            {`${status.jetzt.schicht}schicht · Probe ${status.jetzt.zeitfenster} Uhr`}
          </div>
        </div>
        <div className={cn("flex flex-col items-center gap-4 rounded-3xl border-2 border-dashed border-accent/50 bg-surface px-14 py-10", laeuft && "animate-pulse")}>
          <Nfc className="h-24 w-24 text-accent-strong" aria-hidden="true" />
          <div className="text-3xl font-semibold">Chip an den Leser halten</div>
          <div className="text-lg text-ink2">Danach direkt die Probe eintragen.</div>
        </div>
        {hinweis && (
          <div role="alert" className={cn("max-w-xl rounded-2xl p-5 text-left text-lg", hinweis.ton === "fehler" ? "bg-crit/10" : "bg-surface2")}>
            <p className="m-0">{hinweis.text}</p>
            {hinweis.chipnummer && (
              <p className="m-0 mt-3 text-base text-ink2">
                Chipnummer: <span className="select-all rounded bg-surface px-2 py-1 font-mono text-2xl text-ink">{hinweis.chipnummer}</span>
              </p>
            )}
          </div>
        )}
        {manuell ? (
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              void anmelden(nummer)
            }}
          >
            <input
              autoFocus
              value={nummer}
              onChange={(e) => setNummer(e.target.value)}
              aria-label="Chipnummer"
              className="h-14 w-64 rounded-xl border border-hair/15 bg-surface px-4 font-mono text-xl"
            />
            <button type="submit" className="h-14 rounded-xl bg-accent-strong px-5 text-lg font-semibold text-white">Anmelden</button>
            <button type="button" onClick={() => setManuell(false)} className="h-14 rounded-xl px-4 text-ink2">Abbrechen</button>
          </form>
        ) : (
          <button type="button" onClick={() => setManuell(true)} className="text-sm text-ink2 underline underline-offset-4">
            Kein Leser? Chipnummer eintippen
          </button>
        )}
      </div>
    </main>
  )
}
